import {Injectable} from '@angular/core';
import {RequesterService} from '../../services/requester.service';
import {TasksModel} from './models/tasks.model';
import {Router} from '@angular/router';
import {NavService} from '../../services/nav.service';
import {EventsService} from '../../services/events.service';
import {AnalyticsService} from '../../services/analytics.service';
import {environment} from '../../../environments/environment';
import {UserService} from '../../services/user.service';

@Injectable({
  providedIn: 'root'
})
export class TasksService {
  constructor(
    private analytics: AnalyticsService,
    private event: EventsService,
    private router: Router,
    private linkSystem: NavService,
    private requesterService: RequesterService,
    private userService: UserService
  ) {
  }

  getMyTasksList(): Promise<TasksModel[]> {
    return this.requesterService.get('getMyTasksList')
      .then(data => {
        if (!Array.isArray(data.result)) {
          return [];
        }
        return (data.result as TasksModel[]);
      })
      .catch(e => {
        return [];
      });
  }

  getMyUpcomingEventsList(days: number = null): Promise<TasksModel[]> {
    return this.requesterService.get('getMyUpcomingEventsList', {days: days})
      .then(data => {
        if (!Array.isArray(data.result)) {
          return [];
        }
        return (data.result as TasksModel[]);
      })
      .catch(e => {
        return [];
      });
  }

  getMyTaskCalendar(task: TasksModel): Promise<any> {
    return this.requesterService.get('getMyTaskCalendar', {task_id: task.id})
      .then(data => {
        if (data && data.result && data.result.content) {
          const blob = new Blob([data.result.content], {type: data.result.mime_type});
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = data.result.file;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }
      });
  }

  updateTaskStatus(task: TasksModel) {
    if (task.lock_for_completion === true) {
      return false;
    }

    if (task.section === 'pdr_feedback') {
      return false;
    }

    const params = {
      status: !task.status,
      section_id: task.id,
      del_id: task.del_id
    };

    this.analytics.trackEvent('task_update', {
      task_id: task.id.toString(),
      task_title: task.title,
      task_icon: task.icon,
      task_type: task.section,
      task_uid: task.uid,
      task_url: task.url,
      task_status: params.status ? 'confirmed' : 'unconfirmed',
    });

    return this.requesterService.put('updateTaskStatus', params)
      .then(data => {
        this.event.publish('tasks:updateTaskStatus', task);
      });
  }

  updateTaskClick(task: TasksModel) {
    if (task.lock_for_completion === true) {
      return false;
    }

    if (task.section === 'pdr_feedback') {
      return false;
    }

    const params = {
      section_id: task.id,
      del_id: task.del_id
    };

    this.analytics.trackEvent('task_click', {
      task_id: task.id.toString(),
      task_title: task.title,
      task_icon: task.icon,
      task_type: task.section,
      task_uid: task.uid,
      task_url: task.url,
      task_score: task.score,
      task_status: task.url ? 'confirmed' : 'unconfirmed',
    });

    task.status = true;
    task.click = true;
    return this.requesterService.put('updateTaskClick', params)
      .then(data => {
        this.event.publish('tasks:updateTaskClick', task);
        this.event.publish('tasks:updateTaskStatus', task);
      });
  }

  runTask(task: TasksModel) {

    this.analytics.trackEvent('task_view', {
      task_id: task.id.toString(),
      task_title: task.title,
      task_icon: task.icon,
      task_type: task.section,
      task_uid: task.uid,
      task_url: task.url,
    });

    if (task.section === 'webinar') {
      if (task.embed_video) {
        this.router.navigate(['/conference/' + task.id]);
      } else {
        this.updateTaskClick(task);
        this.linkSystem.toLink(task.url);
      }
    }

    if (['link', 'file'].indexOf(task.section) >= 0) {
      if (!!task.url) {
        if (task.url === environment.websiteUrl + '/journey/simulator/tdm') {
          this.router.navigate(['/simulator/tdm']);
          return true;
        }

        if (task.url === environment.websiteUrl + '/journey/simulator/edm') {
          this.router.navigate(['/simulator/edm']);
          return true;
        }

        if (task.url.indexOf('https://forms.office.com') >= 0) {
          this.updateTaskClick(task);
          this.router.navigate(['/journey/task', task.id]);
          return true;
        }

        this.updateTaskClick(task);

        if (task.url.indexOf('https://www.alchemistlearningjourney.com') === 0) {
          const url = task.url.replace('https://www.alchemistlearningjourney.com', '');
          this.router.navigateByUrl(url);
          return true;
        }

        this.linkSystem.toLink(task.url);
        return true;
      }
    }

    if (task.for_manager) {
      if (task.section === 'sc') {
        this.router.navigate(['/skills-checker/team']);
      } else if (task.section === 'pre_training_obj') {
        this.router.navigate(['/journey/diary', task.program.prog_del_id]);
      } else if (task.section === 'elearning') {
        // this.router.navigate(['/e-learning/report', task.program.delegateId]);
      } else if (task.section === 'coaching') {
        this.router.navigate(['/coaching/team']);
      } else if (task.section === 'coach_incoming_requests') {
        this.router.navigate(['/coach/requests-incoming']);
      } else if (task.section === 'coach_availability') {
        this.router.navigate(['/coach/availability']);
      } else if (task.section === 'review') {
        this.router.navigate(['/review/team/', task.del_id]);
      }
    } else {
      if (task.section === 'sc') {
        this.router.navigate(['/skills-checker']);
      } else if (task.section === 'pre_training_obj') {
        this.router.navigate(['/journey', task.program.stage_index]);
      } else if (task.section === 'elearning') {
        this.router.navigate(['/e-learning/report', task.program.prog_del_id]);
      } else if (task.section === 'coaching') {
        this.router.navigate(['/coaching/my/comments']);
      } else if (['custom_textbox', 'custom_feedback', 'roi', 'file'].indexOf(task.section) >= 0) {
        this.userService.setUserDelegateTemplate(task.template_id);
        this.router.navigate(['/journey', task.stage_index]);
      } else if (task.section === 'coach_appointments') {
        this.router.navigate(['/coach/appointments']);
      } else if (task.section === 'coach_profiles') {
        if (task.details.template_coach && task.details.template_coach_title) {
          this.router.navigate(['/coach/profile/' + task.details.template_coach.id + '/calendar/' + task.details.template_coach_title.id]);
        } else {
          this.router.navigate(['/coach/profiles']);
        }
      } else if (task.section === 'pdr_feedback') {
        this.router.navigate(['/review/feedback', task.uid]);
      } else if (task.section === 'review') {
        this.router.navigate(['/review']);
      }
    }
  }
}
